import { useEffect, useRef, useState } from "react";

import { useSnackbar } from "notistack";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";

import { useChatAssistantAttachments } from "../contexts/useChatAssistantAttachments";

const useChatPopup = ({ systemPrompt }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [showPopup, setShowPopup] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  const { listening, transcript, resetTranscript, browserSupportsSpeechRecognition, isMicrophoneAvailable } =
    useSpeechRecognition();
  const { promptName: contextPromptName } = useChatAssistantAttachments();

  // Use context promptName if available, otherwise fall back to systemPrompt prop
  const effectivePromptName = contextPromptName || systemPrompt;

  const ELAPSED_TIME_LIMIT = 30;

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      handleError("Browser doesn't support speech recognition");
    }
  }, [browserSupportsSpeechRecognition]);

  useEffect(() => {
    if (transcript) {
      setCurrentTranscript(transcript);
    }
  }, [transcript]);

  const startTimer = () => {
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
    const startTime = Date.now();
    setElapsedTime(0);

    const updateTimer = () => {
      const currentTime = Date.now();
      const elapsed = (currentTime - startTime) / 1000;
      setElapsedTime(Math.round(elapsed));
      timerRef.current = requestAnimationFrame(updateTimer);
    };

    timerRef.current = requestAnimationFrame(updateTimer);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleStartRecording = async () => {
    if (listening) return;
    if (!isMicrophoneAvailable) {
      handleError("Microphone not available please allow access to microphone");
      return;
    }
    resetTranscript();
    setCurrentTranscript("");
    startTimer();
    SpeechRecognition.startListening({ continuous: true });
  };

  const handleStopRecording = () => {
    SpeechRecognition.stopListening();
    stopTimer();
  };

  useEffect(() => {
    if (elapsedTime >= ELAPSED_TIME_LIMIT && listening) {
      handleStopRecording();
      handleError("You have reached the maximum recording time of 30 seconds");
      return;
    }
  }, [elapsedTime, listening]);

  // Block/unblock body scroll when popup is shown/hidden
  useEffect(() => {
    if (showPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [showPopup]);

  return {
    showPopup,
    setShowPopup,
    currentTranscript,
    listening,
    elapsedTime,
    handleStartRecording,
    handleStopRecording,
    resetTranscript,
    promptName: effectivePromptName,
  };
};

export default useChatPopup;
